import type { DraftPayload } from '@hypema/shared';
import { EXT_MSG } from '@hypema/shared';

const PING_TIMEOUT = 5000;
const DRAFT_TIMEOUT = 30000; // 30s — compose+fill+autosave+close takes ~10s

export async function isExtensionConnected(): Promise<boolean> {
  return new Promise((resolve) => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === EXT_MSG.PONG) {
        window.removeEventListener('message', handler);
        resolve(true);
      }
    };
    window.addEventListener('message', handler);
    window.postMessage({ type: EXT_MSG.PING }, '*');
    setTimeout(() => {
      window.removeEventListener('message', handler);
      resolve(false);
    }, PING_TIMEOUT);
  });
}

export async function sendDraftRequest(
  payload: DraftPayload
): Promise<{ success: boolean; sent?: boolean; error?: string }> {
  const id = Date.now();
  return new Promise((resolve) => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === EXT_MSG.DRAFT_RESULT && event.data?.id === id) {
        window.removeEventListener('message', handler);
        resolve({
          success: event.data.success,
          sent: event.data.sent,
          error: event.data.error,
        });
      }
    };
    window.addEventListener('message', handler);
    window.postMessage({ type: EXT_MSG.DRAFT_REQUEST, id, payload }, '*');
    // No timeout — user may take minutes to review and send each email
  });
}

export function sendBatchStart(total: number, returnUrl?: string): void {
  window.postMessage({ type: 'HYPEMA_BATCH_START', total, returnUrl: returnUrl || window.location.href }, '*');
}

export function sendBatchEnd(results: { created: number; skipped: number; failed: number }): void {
  window.postMessage({ type: 'HYPEMA_BATCH_END', results }, '*');
}

export interface SentLeadForScan {
  leadId: string;
  recipientEmail: string;
  subject: string;
}

export interface ReplyResult {
  leadId: string;
  repliedAt: string;
}

export function syncReplies(sentLeads: SentLeadForScan[]): Promise<ReplyResult[]> {
  return new Promise((resolve) => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === EXT_MSG.REPLY_RESULTS) {
        window.removeEventListener('message', handler);
        resolve(event.data.replies || []);
      }
    };
    window.addEventListener('message', handler);
    window.postMessage({ type: EXT_MSG.SYNC_REPLIES, sentLeads }, '*');

    // Timeout after 10s — don't hang if extension isn't available
    setTimeout(() => {
      window.removeEventListener('message', handler);
      resolve([]);
    }, 10000);
  });
}
